import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, IconButton, DialogActions, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useSelector, useDispatch } from '../../../../../redux/store';
import CloseIconButton from '../../../../../reusable-components/basic-buttons/CloseIconButton';
import {
  postAddProductImage,
  updateProductFormData,
  duplicateItemData,
} from '../../../../../api/catalog';
import EditItem from './EditItem';
import { checkFieldsForEmptyValues } from '../../../../../utils/validate';
import { getProductDetails, updateSingleProductItemDetails } from '../../../../../redux/slices/catalog';
import { useSnackbar } from '../../../../../reusable-components/snackbar';
import { AwaitButton } from '../../../../../reusable-components/await-button';
import { updateOrderItemFullInRedux } from '../../../../../redux/slices/orders';

EditItemDialog.propTypes = {
  data: PropTypes.array,
  id: PropTypes.number,
  pageId: PropTypes.string,
  vendorItemId: PropTypes.string,
  productId: PropTypes.number,
  description: PropTypes.string,
  agoraCategoryId: PropTypes.number,
  agoraSubcategoryId: PropTypes.number,
  notes: PropTypes.string,
  files: PropTypes.array,
  categories: PropTypes.array,
  editParent: PropTypes.bool,
  row: PropTypes.object,
};

export default function EditItemDialog({
  editParent,
  data,
  id,
  description,
  productId,
  notes,
  files,
  agoraCategoryId,
  agoraSubcategoryId,
  categories,
  row,
  pageId,
}) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [allItems, setAllItems] = useState();
  const [disabled, setDisabled] = useState(false);
  const [ppdOptions, setPpdOptions] = useState([]);
  const [textFieldUrl, setTextFieldUrl] = useState('');
  const [urlError, setUrlError] = useState(false);
  const { orderById } = useSelector((state) => state.orders || {});
  const { unsavedChanges } = orderById[id] || {};
  const { enqueueSnackbar } = useSnackbar();
  const {
    data: { products },
  } = useSelector((state) => state.catalog);

  const checkDuplicateItem = async (vendorId, vendorItemId, itemId) => {
    const response = await duplicateItemData(vendorId, vendorItemId, itemId);
    if (response.status === 200) {
      if (response.data === true) {
        enqueueSnackbar('Item already exists with same vendor and vendor id', { variant: 'error' });
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    if (data) {
      setAllItems(data.map((item) => ({ ...item, price: Number(item.price).toFixed(2) })));
      setPpdOptions(data.filter((item) => item.id === id)[0]?.ppdFacilities || []);
    }
  }, [data]);

  useEffect(() => {
    if (!allItems?.length) return;

    const selectedItem = allItems.find((item) => item.id === id);

    if (!selectedItem) {
      setDisabled(true);
    } else {
      const mainFields = ['price', 'vendor', 'vendorId', 'uomAmount', 'uomType', 'vendorItemId'];
      const uomFields = ['price', 'uomAmount', 'uomType'];

      const isDisabled =
        checkFieldsForEmptyValues(selectedItem, mainFields) ||
        (selectedItem.productDetailUOM && checkFieldsForEmptyValues(selectedItem.productDetailUOM, uomFields));
      setDisabled(isDisabled);
    }
  }, [allItems, id]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  const urlPattern = /^(https?:\/\/|ftps?:\/\/)/;
  const saveProduct = async () => {
    let updatedItems = allItems.map((item) => {
      if (item.id === id && textFieldUrl) {
        return {
          ...item,
          pictures: [...item.pictures, textFieldUrl],
        };
      }
      return item;
    });

    const isDuplicate = await checkDuplicateItem(
      updatedItems.filter((item) => item.id === id)[0]?.vendorId,
      updatedItems.filter((item) => item.id === id)[0]?.vendorItemId,
      updatedItems.filter((item) => item.id === id)[0]?.id
    );

    if (isDuplicate) return;
    let picUrls = updatedItems.filter((item) => item.id === id)[0]?.pictures;
    const processedUrls = await Promise.all(
      picUrls.map(async (url) => {
        try {
          if (!url.includes('//res.cloudinary.com/agoraserv') && urlPattern.test(url)) {
            const imgUrl = await postAddProductImage(url);
            return imgUrl?.data?.length ? imgUrl.data : null;
          }
          return url;
        } catch (err) {
          console.error('Upload failed for URL:', url, err);
          return null;
        }
      })
    );
    picUrls = processedUrls.filter((url) => url !== null);

    updatedItems = updatedItems.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          ppdFacilities: ppdOptions,
          pictures: picUrls,
        };
      }
      return item;
    });

    const response = await updateProductFormData({
      description,
      productId,
      allItems: updatedItems,
      notes,
      files,
      agoraCategoryId,
      agoraSubcategoryId,
      categories,
    });
    if (response.status === 200) {
      if (products.length) {
        dispatch(updateSingleProductItemDetails(response.data));
      }
      dispatch(getProductDetails(response.data.productId, false));
      enqueueSnackbar('Item updated successfully', { variant: 'success' });
      if (row) dispatch(updateOrderItemFullInRedux(pageId, row.id));
    } else {
      enqueueSnackbar('Error Updating Item', { variant: 'error' });
    }

    setOpen(false);
    setTextFieldUrl('');
  };
  return (
    <div>
      <Tooltip title="Edit">
        <IconButton color="secondary" onClick={handleClickOpen} disabled={editParent || unsavedChanges}>
          <EditIcon />
        </IconButton>
      </Tooltip>

      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleClose();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
          }}
        >
          Edit item
         <CloseIconButton onClick={handleClose}/>
        </DialogTitle>
        <DialogContent sx={{ width: '100%' }}>
          <EditItem
            allItems={allItems}
            setAllItems={setAllItems}
            id={id}
            ppdOptions={ppdOptions}
            setPpdOptions={setPpdOptions}
            setTextFieldUrl={setTextFieldUrl}
            textFieldUrl={textFieldUrl}
            error={urlError}
            setError={setUrlError}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', mb: 2 }}>
          <AwaitButton
            variant="contained"
            color="primary"
            sx={{ width: '200px', boxShadow: 3, fontWeight: 'bold' }}
            onClick={saveProduct}
            disabled={disabled || urlError}
          >
            Save
          </AwaitButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}
